import React, { useState } from 'react';
import { Eye, EyeOff, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { authApi, hasAuthToken } from '../../api';
import { useTranslation } from '../../lib/i18n';

type PwField = 'current' | 'new' | 'confirm';

const PasswordChange: React.FC = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<Record<PwField, boolean>>({
    current: false,
    new: false,
    confirm: false,
  });
  const [values, setValues] = useState<Record<PwField, string>>({ current: '', new: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!values.current || !values.new || !values.confirm) {
      setError('Please fill in all password fields.');
      return;
    }
    if (values.new !== values.confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (values.new === values.current) {
      setError('The new password must be different from the current one.');
      return;
    }
    if (!hasAuthToken()) {
      setError('Password changes require a signed-in backend session.');
      return;
    }

    setSubmitting(true);
    try {
      await authApi.changePassword({ currentPassword: values.current, newPassword: values.new });
      setSuccess(true);
      setValues({ current: '', new: '', confirm: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the password.');
    } finally {
      setSubmitting(false);
    }
  };

  const fields: { key: PwField; label: string; placeholder: string }[] = [
    { key: 'current', label: t('settingsForm.currentPassword'), placeholder: t('settingsForm.currentPassword') },
    { key: 'new', label: t('settingsForm.newPassword'), placeholder: t('settingsForm.newPassword') },
    { key: 'confirm', label: t('settingsForm.confirmPassword'), placeholder: t('settingsForm.confirmPassword') },
  ];

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-md space-y-4">
      {fields.map(({ key, label, placeholder }) => (
        <div key={key}>
          <label className="app-label">{label}</label>
          <div className="relative">
            <input
              type={showPassword[key] ? 'text' : 'password'}
              className="app-field pr-10"
              placeholder={placeholder}
              value={values[key]}
              autoComplete={key === 'current' ? 'current-password' : 'new-password'}
              onChange={(e) => {
                setValues((v) => ({ ...v, [key]: e.target.value }));
                setError(null);
                setSuccess(false);
              }}
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
              onClick={() => setShowPassword((prev) => ({ ...prev, [key]: !prev[key] }))}
              aria-label={showPassword[key] ? t('login.hidePassword') : t('login.showPassword')}
            >
              {showPassword[key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      ))}

      {error && (
        <p className="inline-flex items-center gap-1.5 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </p>
      )}
      {success && (
        <p className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          Password updated successfully.
        </p>
      )}

      <div className="pt-2">
        <button type="submit" disabled={submitting} className="app-btn-primary inline-flex w-full items-center justify-center gap-2 sm:w-auto">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? t('common.loading') : t('settingsForm.changePassword')}
        </button>
      </div>
    </form>
  );
};

export default PasswordChange;
